import { getRandomColor } from 'module/helpers/get-random-color';
import { useTranslate } from 'module/hooks/use-translation';
import { logger } from 'module/logger';
import { Loading } from 'module/ui/components/atoms/loading';
import {
  faArrowUpRight,
  faPage,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from '@rocketlane/rocket-router';
import { Button, EmptyState, Icon } from '@rocketlane/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  DocumentationAgent,
  type CreateCompanyPageFromAgentPayload,
} from 'api/document-agent/types';
import { PageTemplateAPI } from 'api/page-template';
import { PageType } from 'api/page-template/types';
import { PagesAPI } from 'api/pages';
import { useGetCompanyPageTemplateQuery } from 'queries/company-page-template';
import {
  useGetDocumentationAgentsQuery,
  useCreateCompanyPageFromAgent,
} from 'queries/document-agent';
import { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { ROUTE_PATHS } from 'routes/route-paths';
import styled from 'styled-components';
import {
  AgentDataCollectionForm,
  type AgentFormData,
} from 'components/organisms/agent-data-collection-form';
import { SourceErrorModals } from './source-error-modals';
/** Error cause shape when invoke agent returns source count validation */
interface InvokeAgentSourceErrorCause {
  emailEngagementCount: number;
  meetingEngagementCount: number;
}

function isInvokeAgentSourceErrorCause(
  value: unknown,
): value is InvokeAgentSourceErrorCause {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const o = value as Record<string, unknown>;

  return (
    typeof o.emailEngagementCount === 'number' &&
    typeof o.meetingEngagementCount === 'number'
  );
}

const Container = styled.div<{ isEmpty?: boolean }>`
  padding: var(--layout-05) var(--layout-06);
  height: 100%;
  background: var(--background-primary);
  min-width: 768px;
  display: ${(props) => (props.isEmpty ? 'flex' : '')};
  justify-content: ${(props) => (props.isEmpty ? 'center' : '')};
  align-items: ${(props) => (props.isEmpty ? 'center' : '')};
`;

const Section = styled.div`
  margin-bottom: var(--spacing-08);
`;

const ScratchCardGrid = styled.div`
  display: grid;
  grid-template-columns: auto;
  gap: var(--base-unit);
  justify-content: start;
  max-width: 1200px;
  margin-bottom: var(--spacing-06);
  margin-top: var(--spacing-06);
`;

const NewDocumentName = styled.div`
  margin-top: var(--spacing-05);
`;

const AgentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, auto);
  gap: var(--base-unit);
  justify-content: start;
  max-width: 1200px;
`;

const AgentCard = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: var(--spacing-04);
  border: 1px solid var(--black-10);
  border-radius: var(--border-radius-small);
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all var(--animation-duration-medium) ease;
  text-align: left;
  width: 250px;
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

  &:hover {
    box-shadow: ${(props) => (props.disabled ? 'none' : 'var(--shadow-03)')};
  }
`;

const AgentIcon = styled.div<{ color: string }>`
  width: var(--avatar-size-base);
  height: var(--avatar-size-base);
  border-radius: 50%;
  background: ${(props) => props.color};
  margin-bottom: var(--spacing-05);
`;

const AgentName = styled.div`
  font-size: var(--font-size-base);
  color: var(--text-primary);
  line-height: var(--line-height-04);
`;

const ExploreMoreButton = styled.div`
  max-width: 1200px;

  button {
    min-width: fit-content;
  }
`;

const LoadingContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 100px 0;
  font-size: var(--font-size-base);
  color: var(--text-tertiary);
`;

const ErrorContainer = styled.div`
  padding: var(--height-base);
  color: var(--error-color);
  text-align: center;
  font-size: var(--font-size-base);
  background: var(--red-10);
  border-radius: var(--border-radius-small);
  max-width: 600px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 70vh;
  align-items: center;
  overflow-y: auto;
`;

const HelperText = styled.div`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-05);
`;

const EmptyAgentsWithManualColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-06);
  width: 100%;
  max-width: 520px;
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-04);
  width: 100%;
  max-width: 360px;
  color: var(--text-tertiary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.06em;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--divider-default);
  }
`;

interface AgentSelectionScreenProps {
  companyId: number;
  projectId?: number;
  isModuleFed?: boolean;
}

export const AgentSelectionScreen = ({
  companyId,
  projectId,
  isModuleFed,
}: AgentSelectionScreenProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = useTranslate();
  const [offset, setOffset] = useState(0);
  const [allAgents, setAllAgents] = useState<DocumentationAgent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<DocumentationAgent | null>(
    null,
  );
  const [isCreating, setIsCreating] = useState(false);
  const [isAnyCardClicked, setIsAnyCardClicked] = useState(false);
  const [sourceErrorModal, setSourceErrorModal] = useState<
    'incomplete' | 'no_source' | null
  >(null);
  const [sourceErrorData, setSourceErrorData] = useState<{
    emailEngagementCount: number;
    meetingEngagementCount: number;
  } | null>(null);
  const [pendingRetryParams, setPendingRetryParams] = useState<{
    sourceId: number;
    documentAgentId: number;
    payload: CreateCompanyPageFromAgentPayload;
    objectType: 'COMPANY' | 'PROJECT';
  } | null>(null);
  const lastInvokeParamsRef = useRef<{
    sourceId: number;
    documentAgentId: number;
    payload: CreateCompanyPageFromAgentPayload;
    objectType: 'COMPANY' | 'PROJECT';
  } | null>(null);
  const lastInvokeSourcesRef = useRef<{
    hasEmailSource: boolean;
    hasMeetingSource: boolean;
  } | null>(null);
  const limit = 6;
  const isProjectContext = projectId != null;
  const sourceId = isProjectContext ? projectId : companyId;
  const objectType: 'COMPANY' | 'PROJECT' = isProjectContext
    ? 'PROJECT'
    : 'COMPANY';

  const { data, isLoading, isFetching, error } = useGetDocumentationAgentsQuery(
    limit,
    offset,
  );

  const selectedAgentTemplateId = selectedAgent?.configuration?.templateId;
  const { data: companyPageTemplate, isLoading: isCompanyPageTemplateLoading } =
    useGetCompanyPageTemplateQuery(selectedAgentTemplateId ?? 0, {
      enabled: !!selectedAgentTemplateId,
    });

  const pageTypeList = isProjectContext
    ? `${PageType.ProjectNitro},${PageType.ProjectManual}`
    : `${PageType.Company},${PageType.CompanyManual},${PageType.ProjectNitro},${PageType.ProjectManual}`;

  const _createCompanyPageMutation = useCreateCompanyPageFromAgent({
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ['pages', 'multiple', sourceId, pageTypeList, objectType],
      });

      if (isModuleFed) {
        navigate(
          `/${ROUTE_PATHS.ACCOUNTS}/${companyId}/nitro-pages/${data.documentId}`,
        );
      } else if (projectId) {
        navigate(`/projects/${projectId}/nitro-pages/${data.documentId}`);
      } else {
        navigate(`/accounts/${companyId}/spaces/${data.documentId}`);
      }
    },
    onError: (error: Error & { cause?: unknown }) => {
      const cause = error?.cause;

      if (!isInvokeAgentSourceErrorCause(cause)) {
        logger.error('Failed to create document', { error });

        return;
      }

      const data = cause;
      const sources = lastInvokeSourcesRef.current;

      const allEnabledSourcesZero =
        (!sources?.hasEmailSource || data.emailEngagementCount === 0) &&
        (!sources?.hasMeetingSource || data.meetingEngagementCount === 0);

      const hasEnabledSourceZero =
        (sources?.hasEmailSource && data.emailEngagementCount === 0) ||
        (sources?.hasMeetingSource && data.meetingEngagementCount === 0);
      const hasEnabledSourcePositive =
        (sources?.hasEmailSource && data.emailEngagementCount > 0) ||
        (sources?.hasMeetingSource && data.meetingEngagementCount > 0);
      const incomplete = hasEnabledSourceZero && hasEnabledSourcePositive;

      const showNoSourceModal = allEnabledSourcesZero;
      const showIncompleteModal = incomplete;

      setSourceErrorData(data);
      setSourceErrorModal(
        showNoSourceModal
          ? 'no_source'
          : showIncompleteModal
            ? 'incomplete'
            : null,
      );
      setPendingRetryParams(lastInvokeParamsRef.current ?? null);
    },
  });

  useEffect(() => {
    if (data?.data) {
      if (offset === 0) {
        setAllAgents(data.data);
      } else {
        setAllAgents((prev) => [...prev, ...data.data]);
      }
    }
  }, [data, offset]);

  const handleExploreMore = () => {
    setOffset((prev) => prev + limit);
  };

  const handleAgentClick = async (agent: DocumentationAgent) => {
    const hasFileInputFields = agent.input_fields.some(
      (field) => field.field_type === 'file_upload',
    );

    setIsAnyCardClicked(true);
    if (
      agent.configuration.sourceType === 'TEMPLATE' &&
      !agent.configuration.acceptSourcesAtRuntime &&
      !hasFileInputFields
    ) {
      const hasMeetingSource = agent.configuration?.sources?.some(
        (source) => source.pageType?.toLowerCase() === 'meeting',
      );
      const hasEmailSource = agent.configuration?.sources?.some(
        (source) => source.pageType?.toLowerCase() === 'email',
      );

      try {
        const templateId = agent.configuration.templateId;
        const template = templateId
          ? await queryClient.ensureQueryData({
              queryKey: ['page-template', templateId],
              queryFn: () => PageTemplateAPI.getPageTemplate(templateId),
            })
          : undefined;
        const documentTitle =
          (template as { templateName?: string } | undefined)?.templateName ??
          t('agent_selection.document_title');

        const payload: CreateCompanyPageFromAgentPayload = {
          title: documentTitle,
          content: {
            type: 'doc',
            content: [],
          },
        };

        lastInvokeParamsRef.current = {
          sourceId,
          documentAgentId: agent.id,
          payload,
          objectType,
        };
        lastInvokeSourcesRef.current = { hasEmailSource, hasMeetingSource };

        await _createCompanyPageMutation.mutateAsync({
          sourceId,
          documentAgentId: agent.id,
          payload,
          confirmed: false,
          objectType,
        });
      } catch (err) {
        logger.error(t('agent_selection.error_create_document'), {
          error: err,
        });
        setIsCreating(false);
        setIsAnyCardClicked(false);
      }
    } else {
      setSelectedAgent(agent);
    }
  };

  const handleCancel = () => {
    setSelectedAgent(null);
    setIsCreating(false);
    setIsAnyCardClicked(false);
  };

  const handleFormSubmit = async (_formData: AgentFormData) => {
    if (!selectedAgent) {
      return;
    }

    // Check which sources are configured for this agent
    const hasMeetingSource = selectedAgent.configuration?.sources?.some(
      (source) => source.pageType?.toLowerCase() === 'meeting',
    );
    const hasEmailSource = selectedAgent.configuration?.sources?.some(
      (source) => source.pageType?.toLowerCase() === 'email',
    );

    // Build sourcesByType conditionally
    const sourcesByType: Record<string, number[]> = {};

    if (hasMeetingSource) {
      sourcesByType.MEETING = _formData.selectedMeetings;
    }
    if (hasEmailSource) {
      sourcesByType.EMAIL = _formData.selectedEmails;
    }

    // Collect all files from all fields into a single array
    const allFiles: File[] = [];

    if (_formData.files) {
      Object.values(_formData.files).forEach((fileArray) => {
        fileArray.forEach((uploadFile) => {
          allFiles.push(uploadFile.originFileObj);
        });
      });
    }

    const payload: CreateCompanyPageFromAgentPayload = {
      sourcesByType,
      content: {
        type: 'doc',
        content: [],
      },
      title:
        companyPageTemplate?.templateName ??
        t('agent_selection.document_title'),
      files: allFiles.length > 0 ? allFiles : undefined,
    };

    const useConfirmedFlow =
      !selectedAgent.configuration.acceptSourcesAtRuntime;

    if (useConfirmedFlow) {
      lastInvokeParamsRef.current = {
        sourceId,
        documentAgentId: selectedAgent.id,
        payload,
        objectType,
      };
      lastInvokeSourcesRef.current = { hasEmailSource, hasMeetingSource };
    } else {
      lastInvokeParamsRef.current = null;
      lastInvokeSourcesRef.current = null;
    }

    setIsCreating(true);
    try {
      await _createCompanyPageMutation.mutateAsync({
        sourceId,
        documentAgentId: selectedAgent.id,
        payload,
        objectType,
        ...(useConfirmedFlow && { confirmed: false }),
      });
    } catch (err: unknown) {
      const error = err as Error & {
        cause?: unknown;
        response?: { data?: unknown };
      };
      const rawCause = error?.cause ?? error?.response?.data;
      const emailCount =
        typeof (rawCause as any)?.emailEngagementCount === 'number'
          ? (rawCause as any).emailEngagementCount
          : 0;
      const meetingCount =
        typeof (rawCause as any)?.meetingEngagementCount === 'number'
          ? (rawCause as any).meetingEngagementCount
          : 0;
      const sourceData = {
        emailEngagementCount: emailCount,
        meetingEngagementCount: meetingCount,
      };
      const sources = lastInvokeSourcesRef.current;
      const allEnabledSourcesZero =
        (!sources?.hasEmailSource || emailCount === 0) &&
        (!sources?.hasMeetingSource || meetingCount === 0);
      const hasEnabledSourceZero =
        (sources?.hasEmailSource && emailCount === 0) ||
        (sources?.hasMeetingSource && meetingCount === 0);
      const hasEnabledSourcePositive =
        (sources?.hasEmailSource && emailCount > 0) ||
        (sources?.hasMeetingSource && meetingCount > 0);
      const incomplete = hasEnabledSourceZero && hasEnabledSourcePositive;

      setSourceErrorData(sourceData);
      setSourceErrorModal(
        allEnabledSourcesZero
          ? 'no_source'
          : incomplete
            ? 'incomplete'
            : 'no_source',
      );
      setPendingRetryParams(lastInvokeParamsRef.current ?? null);
    } finally {
      setIsCreating(false);
    }
  };

  const closeSourceErrorModal = () => {
    setSourceErrorModal(null);
    setSourceErrorData(null);
    setPendingRetryParams(null);
  };

  const handleConfirmIncompleteCreate = async () => {
    if (!pendingRetryParams) {
      return;
    }

    const {
      sourceId: sid,
      documentAgentId,
      payload: p,
      objectType: ot,
    } = pendingRetryParams;

    closeSourceErrorModal();
    lastInvokeParamsRef.current = null;
    setIsCreating(true);
    try {
      await _createCompanyPageMutation.mutateAsync({
        sourceId: sid,
        documentAgentId,
        payload: p,
        confirmed: true,
        objectType: ot,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const errorAgent =
    pendingRetryParams &&
    allAgents.find((a) => a.id === pendingRetryParams.documentAgentId);
  const hasEmailSourceForModal = errorAgent?.configuration?.sources?.some(
    (source) => source.pageType?.toLowerCase() === 'email',
  );
  const hasMeetingSourceForModal = errorAgent?.configuration?.sources?.some(
    (source) => source.pageType?.toLowerCase() === 'meeting',
  );

  const canLoadMore = data?.hasMore && !isLoading && !error;

  const displayedAgents = offset === 0 ? data?.data || [] : allAgents;
  const isEmptyAgentList = displayedAgents.length === 0 && !isFetching;

  const handleStartFromScratch = async () => {
    setIsAnyCardClicked(true);
    try {
      setIsCreating(true);
      const response = await PagesAPI.createDocument({
        title: t('agent_selection.document_title'),
        content: {
          type: 'doc',
          content: [],
        },
        addMapping: true,
        mappingModel: {
          pageType: isProjectContext ? 'PROJECT_MANUAL' : 'COMPANY_MANUAL',
          sourceId,
        },
      });

      await queryClient.invalidateQueries({
        queryKey: ['pages', 'multiple', sourceId, pageTypeList, objectType],
      });

      if (isModuleFed) {
        navigate(
          `/${ROUTE_PATHS.ACCOUNTS}/${companyId}/nitro-pages/${response.documentId}/edit`,
        );
      } else if (projectId) {
        navigate(
          `/projects/${projectId}/nitro-pages/${response.documentId}/edit`,
        );
      } else {
        navigate(`/accounts/${companyId}/spaces/${response.documentId}/edit`);
      }
    } catch (err) {
      logger.error(t('agent_selection.error_create_scratch'), { error: err });
      setIsAnyCardClicked(false);
    } finally {
      setIsCreating(false);
    }
  };

  const showUserInputForm =
    selectedAgent && selectedAgent.configuration.acceptSourcesAtRuntime;

  const hasAgentInputs =
    selectedAgent &&
    selectedAgent.input_fields &&
    selectedAgent.input_fields.length > 0;

  if (
    (isLoading && offset === 0) ||
    (isCompanyPageTemplateLoading && selectedAgentTemplateId)
  ) {
    return (
      <Wrapper>
        <LoadingContainer>
          <Loading size="large" />
        </LoadingContainer>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Container isEmpty={isEmptyAgentList}>
        {(showUserInputForm || hasAgentInputs) && selectedAgent && (
          <AgentDataCollectionForm
            agentData={selectedAgent}
            companyId={companyId}
            onSubmit={handleFormSubmit}
            onCancel={handleCancel}
            isSubmitting={isCreating}
          />
        )}

        {!selectedAgent && (
          <>
            {isLoading && offset === 0 ? (
              <LoadingContainer>
                {t('agent_selection.loading_agents')}
              </LoadingContainer>
            ) : error ? (
              <ErrorContainer>
                {t('agent_selection.error_loading_agents')}
              </ErrorContainer>
            ) : (
              <>
                {isEmptyAgentList ? (
                  <EmptyAgentsWithManualColumn>
                    <EmptyState
                      icon={{ icon: faPage }}
                      message={
                        <FormattedMessage id="document_agent.empty_state_title" />
                      }
                      description={
                        <FormattedMessage id="document_agent.empty_state" />
                      }
                      primaryAction={{
                        leftIcon: <Icon icon={faArrowUpRight} size=".75x" />,
                        onClick: () =>
                          navigate('/agent-builder/documentation-agents'),
                        children: (
                          <FormattedMessage id="document_agent.empty_state_cta" />
                        ),
                      }}
                    />
                    <OrDivider>
                      <FormattedMessage id="agent_selection.empty_state.or" />
                    </OrDivider>
                    <ScratchCardGrid>
                      <div className="heading-02">
                        {t('agent_selection.get_started')}
                      </div>
                      <AgentCard
                        key="start-from-scratch"
                        onClick={handleStartFromScratch}
                        disabled={isAnyCardClicked || isCreating}
                      >
                        <FontAwesomeIcon icon={faPlus} size="lg" />
                        <NewDocumentName>
                          {t('agent_selection.new_document')}
                        </NewDocumentName>
                      </AgentCard>
                    </ScratchCardGrid>
                  </EmptyAgentsWithManualColumn>
                ) : (
                  <>
                    <Section>
                      <div className="heading-02">
                        {t('agent_selection.get_started')}
                      </div>
                      <ScratchCardGrid>
                        <AgentCard
                          key="start-from-scratch"
                          onClick={handleStartFromScratch}
                          disabled={isAnyCardClicked || isCreating}
                        >
                          <FontAwesomeIcon icon={faPlus} size="lg" />
                          <NewDocumentName>
                            {t('agent_selection.new_document')}
                          </NewDocumentName>
                        </AgentCard>
                      </ScratchCardGrid>
                    </Section>
                    <Section>
                      <HelperText>
                        {t('agent_selection.document_agents')}
                      </HelperText>
                      <AgentGrid>
                        {displayedAgents.map((agent) => (
                          <AgentCard
                            key={agent.id}
                            onClick={() => handleAgentClick(agent)}
                            disabled={isAnyCardClicked || isCreating}
                          >
                            <AgentIcon
                              color={
                                agent.icon_color ||
                                `var(--${getRandomColor(agent.id)}-50)`
                              }
                            />
                            <AgentName>{agent.name}</AgentName>
                          </AgentCard>
                        ))}
                      </AgentGrid>
                    </Section>
                    {isFetching ? (
                      <LoadingContainer>
                        <Loading />
                      </LoadingContainer>
                    ) : (
                      <>
                        {canLoadMore && (
                          <ExploreMoreButton>
                            <Button
                              variant="secondary"
                              onClick={handleExploreMore}
                              loading={isFetching && offset > 0}
                              size="small"
                            >
                              {t('agent_selection.explore_more')}
                            </Button>
                          </ExploreMoreButton>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </Container>

      <SourceErrorModals
        noSourceOpen={sourceErrorModal === 'no_source'}
        incompleteOpen={sourceErrorModal === 'incomplete'}
        sourceErrorData={sourceErrorData}
        hasEmailSourceForModal={!!hasEmailSourceForModal}
        hasMeetingSourceForModal={!!hasMeetingSourceForModal}
        canRetry={!!pendingRetryParams}
        isRetryPending={_createCompanyPageMutation.isPending}
        onClose={closeSourceErrorModal}
        onConfirmCreate={handleConfirmIncompleteCreate}
      />
    </Wrapper>
  );
};
